import React from 'react';
import { NotificationTypes } from '../../../../../../../primary-record-types';
import { getIntl } from '../../../../../../../utils/intl';
import { ConfigurableTable, ConfigurableTableRow } from '../../../../../../v2BaseComponents/ConfigurationTable';
import getName from '../../../../../../../utils/getName';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import useOrganizationMedicalProfiles from '../../../../../../../hooks/api/useOrganizationMedicalProfiles';
import useMyPreferences from '../../../../../../../hooks/api/useMyPreferences';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import MainContentContainer from '../../../../../../v2BaseComponents/MainContentContainer';
import { SideDrawerButtonContainer } from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import { useState, useMemo, useRef } from 'react';

const intl = getIntl('MessagePreferencesSideDrawerContent', {
    cancel: 'Cancel',
    save: 'Save',
    'medical-profile-message': 'Profile Message',
    'medical-profile-activity-comment': 'Activity Comment',
    'medical-profile-note-comment': 'Note Comment',
    email: 'Email',
    push: 'Push Notifications',
});

export type MessagePreferencesSideDrawerContentProps = {
    selectedClientRecordIds: string[];
    onClose: (force: boolean) => void;
};

const notificationTypes: NotificationTypes.MedicalProfileNotificationType[] = [
    'medical-profile-message',
    'medical-profile-activity-comment',
    'medical-profile-note-comment',
];

const deliveryTypes: NotificationTypes.DeliveryType[] = ['email', 'push'];

type ProfilePreferences = {
    medicalProfileId: string;
    profileName: string;
    preferences: NotificationTypes.DeliveryType[][];
};

const MessagePreferencesSideDrawerContent: React.FC<MessagePreferencesSideDrawerContentProps> = ({
    selectedClientRecordIds,
    onClose,
}) => {
    const [isDirty, setIsDirty] = useState(false);
    const [workingPreferences, setWorkingPreferences] = useState<ProfilePreferences[] | null>(null);
    const previousProfilePreferencesRef = useRef<string | null>(null);

    const { clientRecords, isLoadingClientRecords } = useOrganizationClientRecords({});

    const activeClientRecords = useMemo(
        () =>
            clientRecords?.filter(
                (record) =>
                    clientRecordStatusTypeGuard.active(record) &&
                    selectedClientRecordIds.includes(record.organizationClientRecordId),
            ) || [],
        [clientRecords, selectedClientRecordIds],
    );

    const activeMedicalProfileIds = useMemo(
        () => activeClientRecords.map((record) => record.clientMedicalProfileId).filter(Boolean),
        [activeClientRecords],
    );

    const { organizationMedicalProfiles, isLoadingOrganizationMedicalProfiles } = useOrganizationMedicalProfiles({});

    const selectedMedicalProfiles = useMemo(
        () =>
            organizationMedicalProfiles?.filter((profile) =>
                activeMedicalProfileIds.includes(profile.medicalProfileId),
            ) || [],
        [organizationMedicalProfiles, activeMedicalProfileIds],
    );

    const myPreferences = useMyPreferences({
        onUpdatePreferencesSuccess: () => {
            setIsDirty(false);
            onClose(true);
        },
    });

    const profilePreferences = useMemo(() => {
        if (myPreferences.isLoading || !myPreferences.preferences || isLoadingOrganizationMedicalProfiles) {
            return null;
        }

        return selectedMedicalProfiles.map((profile) => {
            const existingPrefs = myPreferences.preferences!.medicalProfilePreferences.find(
                (p) => p.medicalProfileId === profile.medicalProfileId,
            );

            const preferences = existingPrefs
                ? [
                      existingPrefs.preferences['medical-profile-message'] || [],
                      existingPrefs.preferences['medical-profile-activity-comment'] || [],
                      existingPrefs.preferences['medical-profile-note-comment'] || [],
                  ]
                : [[], [], []];

            return {
                medicalProfileId: profile.medicalProfileId,
                profileName: getName(profile.contactDetails),
                preferences,
            };
        });
    }, [
        myPreferences.isLoading,
        myPreferences.preferences,
        selectedMedicalProfiles,
        isLoadingOrganizationMedicalProfiles,
    ]);

    React.useEffect(() => {
        if (profilePreferences && !isDirty) {
            const serialized = JSON.stringify(profilePreferences);
            if (previousProfilePreferencesRef.current !== serialized) {
                previousProfilePreferencesRef.current = serialized;
                setWorkingPreferences(profilePreferences);
            }
        } else if (!profilePreferences) {
            previousProfilePreferencesRef.current = null;
        }
    }, [profilePreferences, isDirty]);

    const resetWorkingValue = () => {
        setIsDirty(false);
        setWorkingPreferences(profilePreferences);
    };

    const handleDeliveryTypeToggle = (
        profileIndex: number,
        notificationTypeIndex: number,
        deliveryType: NotificationTypes.DeliveryType,
        value: boolean,
    ) => {
        if (!workingPreferences) return;

        const newPreferences = [...workingPreferences];
        const profilePrefs = { ...newPreferences[profileIndex] };
        const currentDeliveryTypes = [...profilePrefs.preferences[notificationTypeIndex]];

        if (value) {
            if (!currentDeliveryTypes.includes(deliveryType)) {
                currentDeliveryTypes.push(deliveryType);
            }
        } else {
            const index = currentDeliveryTypes.indexOf(deliveryType);
            if (index > -1) {
                currentDeliveryTypes.splice(index, 1);
            }
        }

        profilePrefs.preferences = [...profilePrefs.preferences];
        profilePrefs.preferences[notificationTypeIndex] = currentDeliveryTypes;
        newPreferences[profileIndex] = profilePrefs;
        setWorkingPreferences(newPreferences);
        setIsDirty(true);
    };

    const handleSelectAllToggle = (profileIndex: number, deliveryType: NotificationTypes.DeliveryType) => {
        if (!workingPreferences) return;

        const currentValue = getSelectAllDeliveryTypeValue(profileIndex, deliveryType);
        const newCheckboxValue = currentValue === 'indeterminate' ? false : !currentValue;

        const newPreferences = [...workingPreferences];
        const profilePrefs = { ...newPreferences[profileIndex] };

        profilePrefs.preferences = profilePrefs.preferences.map((notificationPrefs) => {
            const newPrefs = [...notificationPrefs];
            if (newCheckboxValue) {
                if (!newPrefs.includes(deliveryType)) {
                    newPrefs.push(deliveryType);
                }
            } else {
                const index = newPrefs.indexOf(deliveryType);
                if (index > -1) {
                    newPrefs.splice(index, 1);
                }
            }
            return newPrefs;
        });

        newPreferences[profileIndex] = profilePrefs;
        setWorkingPreferences(newPreferences);
        setIsDirty(true);
    };

    const getSelectAllDeliveryTypeValue = (profileIndex: number, deliveryType: NotificationTypes.DeliveryType) => {
        if (!workingPreferences) return false;

        const profilePrefs = workingPreferences[profileIndex].preferences;
        const allSelected = profilePrefs.every((notificationPrefs) => notificationPrefs.includes(deliveryType));
        const noneSelected = profilePrefs.every((notificationPrefs) => !notificationPrefs.includes(deliveryType));

        if (allSelected) {
            return true;
        } else if (noneSelected) {
            return false;
        } else {
            return 'indeterminate';
        }
    };

    const handleCancel = () => {
        resetWorkingValue();
        onClose(false);
    };

    const handleSave = () => {
        if (workingPreferences && myPreferences.preferences) {
            const workingPreferencesMap = new Map(
                workingPreferences.map((prof) => [prof.medicalProfileId, prof.preferences]),
            );

            const updatedPreferences = myPreferences.preferences.medicalProfilePreferences.map((profilePrefs) => {
                const workingPrefs = workingPreferencesMap.get(profilePrefs.medicalProfileId);
                if (workingPrefs) {
                    return {
                        ...profilePrefs,
                        preferences: {
                            'medical-profile-message': workingPrefs[0],
                            'medical-profile-activity-comment': workingPrefs[1],
                            'medical-profile-note-comment': workingPrefs[2],
                        },
                    };
                }
                return profilePrefs;
            });

            workingPreferences.forEach((workingProfile) => {
                const existsInPreferences = myPreferences.preferences!.medicalProfilePreferences.some(
                    (p) => p.medicalProfileId === workingProfile.medicalProfileId,
                );

                if (!existsInPreferences) {
                    updatedPreferences.push({
                        medicalProfileId: workingProfile.medicalProfileId,
                        preferences: {
                            'medical-profile-message': workingProfile.preferences[0],
                            'medical-profile-activity-comment': workingProfile.preferences[1],
                            'medical-profile-note-comment': workingProfile.preferences[2],
                        },
                    });
                }
            });

            const update = {
                medicalProfilePreferences: updatedPreferences,
            };

            myPreferences.updatePreferences(update);
        }
    };

    const isLoading =
        isLoadingClientRecords ||
        isLoadingOrganizationMedicalProfiles ||
        myPreferences.isLoading ||
        !workingPreferences;

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <SideDrawerButtonContainer
                    buttons={[
                        {
                            title: intl('cancel'),
                            variant: 'secondary',
                            onPress: handleCancel,
                            testID: 'message-preferences-cancel',
                            icon: 'XCircle',
                        },
                        {
                            title: intl('save'),
                            variant: 'primary',
                            onPress: handleSave,
                            isDisabled: !isDirty || myPreferences.isUpdatingPreferences,
                            testID: 'message-preferences-save',
                            icon: 'CheckCircle',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <ContentStack>
                            <ConfigurableTable
                                title=""
                                checkboxTitles={deliveryTypes.map((type) => intl(type))}
                                checkboxWidth={120}
                            >
                                {workingPreferences?.map((profilePref, profileIndex) => (
                                    <React.Fragment key={profilePref.medicalProfileId}>
                                        {/* Profile name row with "Select All" functionality */}
                                        <ConfigurableTableRow
                                            title={profilePref.profileName}
                                            isGroupRow
                                            checkboxes={deliveryTypes.map((deliveryType) => ({
                                                onToggle: () => handleSelectAllToggle(profileIndex, deliveryType),
                                                value: getSelectAllDeliveryTypeValue(profileIndex, deliveryType),
                                                testID: `select-all-checkbox-${profilePref.medicalProfileId}-${deliveryType}`,
                                            }))}
                                        />

                                        {/* Individual notification type rows */}
                                        {notificationTypes.map((notificationType, notificationIndex) => (
                                            <ConfigurableTableRow
                                                key={notificationType}
                                                title={intl(notificationType)}
                                                checkboxes={deliveryTypes.map((deliveryType) => ({
                                                    onToggle: (value) =>
                                                        handleDeliveryTypeToggle(
                                                            profileIndex,
                                                            notificationIndex,
                                                            deliveryType,
                                                            value,
                                                        ),
                                                    value:
                                                        profilePref.preferences[notificationIndex]?.includes(
                                                            deliveryType,
                                                        ) || false,
                                                    testID: `delivery-type-checkbox-${profilePref.medicalProfileId}-${notificationType}-${deliveryType}`,
                                                }))}
                                                isLastInGroupRow={
                                                    notificationIndex === notificationTypes.length - 1 &&
                                                    profileIndex === workingPreferences.length - 1
                                                }
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                            </ConfigurableTable>
                        </ContentStack>
                    </MainContentContainer>
                </SideDrawerButtonContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(MessagePreferencesSideDrawerContent);
